import * as React from "react"
import Link from "next/link"
import { ArrowRightIcon } from "@/components/brand-kit/icon-sprite"

interface CtaBannerProps {
  brandName: string
  /** Where the orange CTA links to — typically `/collections/{slug}` or the
   *  brand hub page when one exists. */
  href: string | null
}

/**
 * Full-width gradient CTA banner between the FAQ and Related Brands.
 *
 * Blue base radial-spotted with a warm orange highlight in the top-left,
 * topped with a subtle 6×4 grid texture for editorial detail (handoff §9).
 * The orange pill CTA links to the brand's collection page.
 */
export function CtaBanner({ brandName, href }: CtaBannerProps) {
  return (
    <section className="my-10">
      <div
        className="relative overflow-hidden rounded-[18px] px-8 py-8 text-center sm:px-12 sm:py-10"
        style={{
          background:
            "radial-gradient(ellipse 60% 80% at 20% 20%, rgba(255,153,81,0.35), transparent 60%), linear-gradient(135deg, #0469f0 0%, #003ad0 100%)",
        }}
      >
        {/* 6×4 grid texture — 5% opacity, no pointer events */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "100px 100px",
          }}
        />
        <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-white/70">
          Ready to shop?
        </p>
        <h2 className="mx-auto mt-1.5 max-w-[760px] text-[22px] font-black leading-[1.05] tracking-[-0.03em] text-white sm:text-[28px]">
          Find Your Perfect {brandName} Refrigerator
        </h2>
        <p className="mx-auto mt-2 max-w-[640px] text-[14px] leading-[1.5] text-white/[0.78]">
          Compare prices from major online appliance retailers — all in one place.
        </p>
        {href && <Link
          href={href}
          prefetch={false}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#ff9951] px-5 py-3 text-[13px] font-extrabold text-[var(--color-brand-action-text)] shadow-[0_12px_24px_-8px_rgba(0,0,0,0.25)] transition-colors hover:bg-[#ff8533]"
        >
          Browse {brandName} Collection
          <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden />
        </Link>}
      </div>
    </section>
  )
}
